import { useId, useMemo, useState, type ReactNode } from 'react'
import {
  montarFichaClinica,
  type DadoClinicoAcao,
  type EtapaAvaliacao,
  type FichaVitimaAtendimento,
  type GrupoFicha,
  type IdAcaoAvaliacao,
} from '../../dominio/tabletop/atendimento'
import './fichaClinica.css'

export type ModoFichaClinica = 'instrutor' | 'turma'
type AbaFicha = 'inicial' | 'secundaria' | 'passagem'

const ABAS: readonly { id: AbaFicha; rotulo: string; soInstrutor?: boolean }[] = [
  { id: 'inicial', rotulo: 'Avaliação inicial (XABCDE)' },
  { id: 'secundaria', rotulo: 'Avaliação secundária' },
  { id: 'passagem', rotulo: 'Resumo para passagem', soInstrutor: true },
]

const ROTULO_TRIAGEM = { vermelho: 'Vermelho', amarelo: 'Amarelo', verde: 'Verde', preto: 'Preto' } as const

interface Props {
  readonly ficha: FichaVitimaAtendimento
  /**
   * instrutor vê tudo, inclusive os estágios futuros; turma só vê o que a
   * equipe já leu (coluna "Leitura da equipe"), sem o bloco do ator, o
   * gabarito e o resumo
   */
  readonly modo: ModoFichaClinica
  /** "inicial" ou o id de um estágio da ficha */
  readonly estagioAtual: string
  /** exames que a equipe já fez e que ainda valem (linha marcada) */
  readonly verificados: ReadonlySet<IdAcaoAvaliacao>
  /** exames lidos cujo valor mudou desde a leitura: o instrutor vê "reavaliar" */
  readonly desatualizados?: ReadonlySet<IdAcaoAvaliacao>
  /** última leitura da equipe por exame; é o que a turma enxerga */
  readonly leituras?: ReadonlyMap<IdAcaoAvaliacao, DadoClinicoAcao>
  readonly aoAlternarVerificado: (idAcao: IdAcaoAvaliacao, marcado: boolean) => void
  /** só o instrutor muda o estágio; sem isso o seletor não aparece */
  readonly aoMudarEstagio?: (idEstagio: string) => void
  /** uma vez marcado, o exame não desmarca (a leitura já foi registrada) */
  readonly travarVerificados?: boolean
  /** conteúdo extra no cabeçalho de cada letra do XABCDE (ex.: interpretação da equipe) */
  readonly acoesDoGrupo?: (etapa: EtapaAvaliacao) => ReactNode
  /** A Oficina mostra os achados atuais, independentemente do check e do modo. */
  readonly leiturasSempreVisiveis?: boolean
  readonly mostrarProgresso?: boolean
  /** Texto contextual após os exames de cada etapa. */
  readonly conteudoDoGrupo?: (etapa: EtapaAvaliacao) => ReactNode
}

export function FichaClinicaVitima({
  ficha,
  modo,
  estagioAtual,
  verificados,
  desatualizados,
  leituras,
  aoAlternarVerificado,
  aoMudarEstagio,
  travarVerificados = false,
  acoesDoGrupo,
  leiturasSempreVisiveis = false,
  mostrarProgresso = true,
  conteudoDoGrupo,
}: Props) {
  const montada = useMemo(() => montarFichaClinica(ficha), [ficha])
  const [abaEscolhida, setAba] = useState<AbaFicha>('inicial')
  const idBase = useId()
  const instrutor = modo === 'instrutor'
  const abas = ABAS.filter((a) => instrutor || !a.soInstrutor)
  const aba = abas.some((a) => a.id === abaEscolhida) ? abaEscolhida : 'inicial'

  const colunasEstagio = montada.estagios
  const idsDaAba = (grupos: readonly GrupoFicha[]) => grupos.flatMap((g) => g.linhas.map((l) => l.idAcao))
  const idsAba = { inicial: idsDaAba(montada.inicial), secundaria: idsDaAba(montada.secundaria), passagem: [] as IdAcaoAvaliacao[] }
  const feitos = idsAba[aba].filter((id) => verificados.has(id)).length

  const triagem = ficha.gabaritoInstrutor?.triagemSugerida

  return (
    <section className="fcv" aria-label={`Ficha clínica de ${ficha.titulo}`}>
      <header className="fcv__cabecalho">
        <h3>{montada.cabecalho.titulo}</h3>
        <p className="fcv__sub"><strong>Posição:</strong> {montada.cabecalho.papel}</p>
        <p className="fcv__sub"><strong>Mecanismo:</strong> {montada.cabecalho.mecanismo}</p>
        {!instrutor && <p className="fcv__sub">Aparência: {montada.ator.aparencia}</p>}
        {instrutor && triagem && (
          <p className="fcv__referencia" data-triagem={triagem}>Triagem de referência: {ROTULO_TRIAGEM[triagem]}</p>
        )}
      </header>

      {instrutor && (
        <dl className="fcv__ator" aria-label="Para o instrutor e o ator">
          <div><dt>Descrição</dt><dd>{montada.ator.descricao}</dd></div>
          <div><dt>Condição clínica</dt><dd>{montada.ator.condicao}</dd></div>
          <div><dt>Aparência</dt><dd>{montada.ator.aparencia}</dd></div>
          <div><dt>Instruções ao ator</dt><dd>{montada.ator.instrucoes}</dd></div>
        </dl>
      )}

      {aoMudarEstagio && instrutor && colunasEstagio.length > 1 && (
        <div className="fcv__estagios" role="group" aria-label="Estágio da vítima">
          <span>Estágio da vítima:</span>
          {colunasEstagio.map((e) => (
            <button key={e.id} type="button" aria-pressed={e.id === estagioAtual} onClick={() => aoMudarEstagio(e.id)}>
              {e.rotulo}
            </button>
          ))}
        </div>
      )}

      <div className="fcv__abas" role="tablist" aria-label="Avaliação da vítima">
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            id={`${idBase}-aba-${a.id}`}
            aria-selected={aba === a.id}
            aria-controls={`${idBase}-painel`}
            onClick={() => setAba(a.id)}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div id={`${idBase}-painel`} role="tabpanel" aria-labelledby={`${idBase}-aba-${aba}`} className="fcv__painel">
        {aba !== 'passagem' ? (
          <>
            {mostrarProgresso && <p className="fcv__progresso" aria-live="polite">{feitos} de {idsAba[aba].length} verificados</p>}
            <TabelaExames
              idBase={idBase}
              grupos={aba === 'inicial' ? montada.inicial : montada.secundaria}
              instrutor={instrutor}
              leiturasSempreVisiveis={leiturasSempreVisiveis}
              colunas={colunasEstagio}
              estagioAtual={estagioAtual}
              verificados={verificados}
              desatualizados={desatualizados}
              leituras={leituras}
              travar={travarVerificados}
              aoAlternar={aoAlternarVerificado}
              acoesDoGrupo={aba === 'inicial' ? acoesDoGrupo : undefined}
              conteudoDoGrupo={conteudoDoGrupo}
            />
          </>
        ) : (
          <Passagem
            linhas={montada.passagem.linhas}
            tratamentos={montada.passagem.tratamentos}
            mostrarTardia={colunasEstagio.length > 1}
          />
        )}
      </div>
    </section>
  )
}

interface PropsTabela {
  idBase: string
  grupos: readonly GrupoFicha[]
  instrutor: boolean
  leiturasSempreVisiveis: boolean
  colunas: readonly { id: string; rotulo: string }[]
  estagioAtual: string
  verificados: ReadonlySet<IdAcaoAvaliacao>
  desatualizados?: ReadonlySet<IdAcaoAvaliacao>
  leituras?: ReadonlyMap<IdAcaoAvaliacao, DadoClinicoAcao>
  travar: boolean
  aoAlternar: (idAcao: IdAcaoAvaliacao, marcado: boolean) => void
  acoesDoGrupo?: (etapa: EtapaAvaliacao) => ReactNode
  conteudoDoGrupo?: (etapa: EtapaAvaliacao) => ReactNode
}

function TabelaExames({ idBase, grupos, instrutor, colunas, estagioAtual, verificados, desatualizados, leituras, travar, aoAlternar, acoesDoGrupo, leiturasSempreVisiveis, conteudoDoGrupo }: PropsTabela) {
  const compararEstagios = instrutor && !leiturasSempreVisiveis
  const colunasVisiveis = compararEstagios ? colunas : [{ id: 'leitura', rotulo: 'Leitura da equipe' }]
  return (
    <div className="fcv__tabela-rolagem">
      <table className="fcv__tabela">
        <thead>
          <tr>
            <th scope="col">Parâmetro</th>
            {colunasVisiveis.map((c) => (
              <th key={c.id} scope="col" aria-current={compararEstagios && c.id === estagioAtual ? 'true' : undefined} className={compararEstagios && c.id === estagioAtual ? 'fcv--atual' : undefined}>
                {c.rotulo}
              </th>
            ))}
            <th scope="col">Verificado</th>
          </tr>
        </thead>
        {grupos.map((g) => {
          const conteudo = conteudoDoGrupo?.(g.etapa)
          return (
            <tbody key={g.etapa}>
              <tr className="fcv__grupo">
                <th scope="rowgroup" colSpan={colunasVisiveis.length + 2} id={`${idBase}-grupo-${g.etapa}`} tabIndex={-1}>
                  <span className="fcv__grupo-titulo">{g.sigla ? <b>{g.sigla}</b> : null}{g.sigla ? ' — ' : ''}{g.rotulo}</span>
                  {acoesDoGrupo ? <span className="fcv__grupo-acoes">{acoesDoGrupo(g.etapa)}</span> : null}
                </th>
              </tr>
              {g.linhas.map((l) => {
                const marcado = verificados.has(l.idAcao)
                const reavaliar = (instrutor || leiturasSempreVisiveis) && !marcado && desatualizados?.has(l.idAcao)
                return (
                  <tr key={l.idAcao} className={marcado ? 'fcv__linha fcv__linha--feita' : 'fcv__linha'}>
                    <th scope="row">{l.rotulo}</th>
                    {compararEstagios ? colunas.map((c, i) => {
                      const cel = l.celulas[i]!
                      return (
                        <td
                          key={c.id}
                          data-estagio={c.rotulo}
                          className={[c.id === estagioAtual ? 'fcv--atual' : '', cel.estado === 'incerto' ? 'fcv--incerto' : '', cel.estado === 'nao_informado' ? 'fcv--vazio' : ''].filter(Boolean).join(' ') || undefined}
                        >
                          {cel.mudou ? <strong>{cel.texto}</strong> : cel.texto}
                          {cel.nota ? <small className="fcv__nota">{cel.nota}</small> : null}
                        </td>
                      )
                    }) : (
                      <LeituraDaEquipe leitura={leituras?.get(l.idAcao)} />
                    )}
                    <td data-estagio="Verificado" className="fcv__check">
                      <label className="fcv__verificacao">
                        <input
                          type="checkbox"
                          aria-label={`Verificado: ${l.rotulo}`}
                          checked={marcado}
                          disabled={travar && marcado}
                          onChange={(ev) => aoAlternar(l.idAcao, ev.target.checked)}
                        />
                        <span>{marcado ? 'Verificado' : 'Verificar'}</span>
                      </label>
                      {reavaliar ? <small className="fcv__reavaliar">Reavaliar</small> : null}
                    </td>
                  </tr>
                )
              })}
              {conteudo && (
                <tr className="fcv__grupo-conteudo">
                  <td colSpan={colunasVisiveis.length + 2}>{conteudo}</td>
                </tr>
              )}
            </tbody>
          )
        })}
      </table>
    </div>
  )
}

function LeituraDaEquipe({ leitura }: { leitura: DadoClinicoAcao | undefined }) {
  if (!leitura) return <td data-estagio="Leitura da equipe" className="fcv--vazio">Não examinado</td>
  return (
    <td data-estagio="Leitura da equipe" className={leitura.estado === 'incerto' ? 'fcv--incerto' : undefined}>
      {leitura.estado === 'nao_informado' ? '—' : leitura.valor}
    </td>
  )
}

function Passagem({
  linhas,
  tratamentos,
  mostrarTardia,
}: {
  linhas: readonly { parametro: string; inicial: string; tardia: string }[]
  tratamentos: readonly string[]
  mostrarTardia: boolean
}) {
  return (
    <>
      <div className="fcv__tabela-rolagem">
        <table className="fcv__tabela">
          <thead>
            <tr>
              <th scope="col">Parâmetro</th>
              <th scope="col">Avaliação inicial</th>
              {mostrarTardia && <th scope="col">Avaliação tardia</th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.parametro}>
                <th scope="row">{l.parametro}</th>
                <td data-estagio="Avaliação inicial" colSpan={mostrarTardia && l.tardia === '' ? 2 : 1}>{l.inicial}</td>
                {mostrarTardia && l.tardia !== '' && <td data-estagio="Avaliação tardia">{l.tardia}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4 className="fcv__subtitulo">Tratamento realizado</h4>
      {tratamentos.length === 0 ? <p>Nenhum tratamento citado na ficha.</p> : (
        <ul className="fcv__tratamentos">{tratamentos.map((t) => <li key={t}>{t}</li>)}</ul>
      )}
    </>
  )
}
